import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { selectCurrentUser } from "../pages/auth/authSlice";
import { useGetPublicSchoolInfoQuery } from "../../app/authApiSlice";
import { useGetSchoolProfileQuery } from "../pages/settings/school/schoolApiSlice";
import { setColor } from "../../theme/themeSlice";

const PATH_TITLES = {
  "/": "Dashboard",
  "/dashboard/edit-profile": "Edit Profile",
  "/classes": "Classes",
  "/staff": "Staff",
  "/subjects": "Subjects",
  "/roles": "Roles",
  "/students": "Students",
  "/settings": "Settings",
  "/score-log": "Score Log",
  "/activity-logs": "Activity Logs",
  "/change-password": "Change Password",
  "/class-broadsheet": "Class Broadsheet",
  "/class-report": "Class Report",
  "/student-report": "Student Report",
  "/ca-manager": "CA Manager",
  "/quick-entry": "Quick Entry",
  "/exam-manager": "Exam Manager",
  "/manage-attendance": "Attendance",
  "/houses": "Houses",
  "/account-settings": "Account Settings",
  "/fees-schedule": "Fees Schedule",
  "/invoices": "Invoices",
  "/payments-dashboard": "Payments",
  "/templates": "Templates",
  "/parent": "Parent Dashboard",
  "/student": "Student Portal",
  "/lms/overview": "LMS Overview",
  "/lms/student/overview": "LMS Overview",
  "/lms/student/notes": "Shared Notes",
  "/lms/student/leaderboard": "Leaderboard",
  "/communication/feed": "Feed",
  "/communication/messages": "Messages",
  "/communication/settings": "Notification Settings",
  "/school-information": "School Information",
};

const PATH_PREFIX_TITLES = [
  { prefix: "/parent/student/", title: "Student Detail" },
  { prefix: "/class-subjects/", title: "Class Subjects" },
  { prefix: "/class-reportsheet/", title: "Class Report Sheet" },
  { prefix: "/section-report/", title: "Section Report" },
  { prefix: "/department-report/", title: "Department Report" },
  { prefix: "/lms/questions", title: "Questions" },
  { prefix: "/lms/notes", title: "Notes" },
  { prefix: "/lms/quizzes", title: "Quizzes" },
  { prefix: "/lms/reviews", title: "Reviews" },
  { prefix: "/lms/student/class-quiz", title: "Class Quiz" },
  { prefix: "/lms/student/quiz", title: "Quiz" },
  { prefix: "/communication/messages/", title: "Messages" },
];

function getPageTitle(pathname) {
  if (PATH_TITLES[pathname]) return PATH_TITLES[pathname];
  const match = PATH_PREFIX_TITLES.find(({ prefix }) =>
    pathname.startsWith(prefix)
  );
  return match?.title ?? null;
}

export default function TitleManager() {
  const location = useLocation();
  const user = useSelector(selectCurrentUser);
  const dispatch = useDispatch();

  const { data: publicSchoolInfo } = useGetPublicSchoolInfoQuery();
  const { data: schoolProfile } = useGetSchoolProfileQuery(undefined, {
    skip: !user,
  });

  useEffect(() => {
    if (!schoolProfile?.color) return;
    const color = schoolProfile.color === "blue" ? "#2574d1" : schoolProfile.color;
    dispatch(setColor({ color }));
  }, [dispatch, schoolProfile?.color]);

  useEffect(() => {
    const pathname = location?.pathname || "/";
    const isLoginPage = pathname === "/login";

    if (isLoginPage) {
      const schoolName = publicSchoolInfo?.name;
      document.title = schoolName ? `Schoola | ${schoolName}` : "Schoola";
      return;
    }

    const pageTitle = getPageTitle(pathname);
    const schoolName = schoolProfile?.name || publicSchoolInfo?.name;

    if (pageTitle && schoolName) {
      document.title = `${pageTitle} | ${schoolName}`;
    } else if (pageTitle) {
      document.title = pageTitle;
    } else if (schoolName) {
      document.title = `Schoola | ${schoolName}`;
    }
  }, [location?.pathname, publicSchoolInfo, schoolProfile]);

  return null;
}
